/**
 * Shared navigation route mapping for desktop/mobile menus.
 */

import { mapActiveMenuIdForGrouping, resolveMobileLevel1MenuId, isFinanceRoute } from './adminNavConfig';

// Order is significant: the first prefix that matches wins, so list specific
// paths (e.g. `/drilling/progress-map`) before broader ones (`/drilling`).
// Old `/operations/field-*`, `/operations/drilling/*` and `/reports/*` prefixes
// are kept while back-compat redirects in QhseOperationsRoutes.js are still
// live, so the sidebar stays highlighted under Drilling during the transient
// `Navigate` round-trip (see Incident 15 and R32 in ARCHITECTURE.md).
export const PATH_TO_LEVEL1_MENU = [
  // Drilling — primary /drilling/* paths (post-2026-05-25 namespace).
  { prefix: '/drilling/dashboard', menuId: 'drilling' },
  { prefix: '/drilling/progress-map', menuId: 'drilling' },
  { prefix: '/drilling/work-planning', menuId: 'drilling' },
  { prefix: '/drilling/work-projection', menuId: 'drilling' },
  { prefix: '/drilling/crew-report-field', menuId: null },
  { prefix: '/drilling/sp-registry', menuId: 'drilling' },
  { prefix: '/drilling/crew-reports', menuId: 'drilling' },
  { prefix: '/drilling/crew-report', menuId: 'drilling' },
  { prefix: '/drilling/npt-log', menuId: 'drilling' },
  { prefix: '/drilling/client-daily-report', menuId: 'drilling' },
  { prefix: '/drilling/cdr-archive', menuId: 'drilling' },
  { prefix: '/drilling/npt-report-archive', menuId: 'drilling' },
  // Cross-area operations.
  { prefix: '/operations/chat', menuId: null },
  { prefix: '/operations/jm', menuId: null },
  { prefix: '/operations/fuel-control', menuId: null },
  { prefix: '/inventory/mobile', menuId: 'inventory-field-entry' },
  // Legacy drilling paths kept while back-compat redirects are still mounted.
  // Drop these once all clients use /drilling/* URLs.
  { prefix: '/operations/field-operations', menuId: 'drilling' },
  { prefix: '/operations/field-planning', menuId: 'drilling' },
  { prefix: '/operations/drilling', menuId: 'drilling' },
  { prefix: '/reports/cdr-archive', menuId: 'drilling' },
  { prefix: '/reports/npt-report-archive', menuId: 'drilling' },
  // Other modules.
  { prefix: '/administration', menuId: 'administration' },
  { prefix: '/supply-chain', menuId: 'supply-chain' },
  { prefix: '/catering', menuId: 'catering' },
  { prefix: '/finance', menuId: 'finance' },
  { prefix: '/inventory', menuId: 'inventory' },
  { prefix: '/hr', menuId: 'hr' },
  { prefix: '/tenders', menuId: 'tenders' },
  { prefix: '/projects', menuId: 'projects' },
  { prefix: '/master-data', menuId: 'settings' },
  { prefix: '/platform', menuId: null },
  { prefix: '/admin', menuId: 'settings' },
  { prefix: '/company/backups', menuId: 'settings' },
  { prefix: '/access-control', menuId: 'settings' },
  { prefix: '/help', menuId: 'help' },
  { prefix: '/survey/map', menuId: 'survey' },
  { prefix: '/survey/planning', menuId: 'survey' },
  { prefix: '/survey/production-report', menuId: 'survey' },
  { prefix: '/budgets/survey', menuId: 'survey' },
  { prefix: '/dailies/survey', menuId: 'survey' },
  { prefix: '/summary/survey', menuId: 'survey' },
  { prefix: '/budgets/drilling', menuId: 'drilling' },
  { prefix: '/dailies/drilling', menuId: 'drilling' },
  { prefix: '/summary/drilling', menuId: 'drilling' },
  { prefix: '/drilling', menuId: 'drilling' },
  { prefix: '/recording/map', menuId: 'recording' },
  { prefix: '/recording/planning', menuId: 'recording' },
  { prefix: '/recording/production-report', menuId: 'recording' },
  { prefix: '/budgets/recording', menuId: 'recording' },
  { prefix: '/dailies/recording', menuId: 'recording' },
  { prefix: '/summary/recording', menuId: 'recording' },
  { prefix: '/budgets/basecamp', menuId: 'basecamp' },
  { prefix: '/budgets/new', menuId: 'basecamp' },
  { prefix: '/dailies/basecamp', menuId: 'basecamp' },
  { prefix: '/summary/basecamp', menuId: 'basecamp' },
  { prefix: '/operations', menuId: 'operations' },
  { prefix: '/qhse', menuId: 'qhse' },
];

export const INVENTORY_FULL_CHILD_ROUTES = [
  '/inventory/dashboard',
  '/inventory/new',
  '/inventory/kits',
  '/inventory/parameters',
  '/inventory/vendors',
];

export const getRouteMenuId = (pathname, search = '') => {
  const full = `${pathname}${search}`;
  if (full.startsWith('/qhse')) return 'qhse';
  // Drilling — both new /drilling/* and legacy paths route to the Drilling menu.
  if (
    full.startsWith('/drilling/') ||
    full === '/drilling' ||
    full.startsWith('/operations/drilling/') ||
    full.startsWith('/operations/field-operations') ||
    full.startsWith('/operations/field-planning') ||
    full.startsWith('/reports/cdr-archive') ||
    full.startsWith('/reports/npt-report-archive') ||
    full.startsWith('/budgets/drilling/') ||
    full.startsWith('/dailies/drilling/') ||
    full.startsWith('/summary/drilling') ||
    (pathname.startsWith('/budgets/management') && search.includes('area=drilling'))
  ) return 'drilling';
  if (
    full.startsWith('/survey/map') ||
    full.startsWith('/survey/planning') ||
    full.startsWith('/survey/production-report') ||
    full.startsWith('/budgets/survey') ||
    full.startsWith('/dailies/survey/') ||
    full.startsWith('/summary/survey') ||
    (pathname.startsWith('/budgets/management') && search.includes('area=survey'))
  ) return 'survey';
  if (
    full.startsWith('/recording/map') ||
    full.startsWith('/recording/planning') ||
    full.startsWith('/recording/production-report') ||
    full.startsWith('/budgets/recording/') ||
    full.startsWith('/dailies/recording/') ||
    full.startsWith('/summary/recording') ||
    (pathname.startsWith('/budgets/management') && search.includes('area=recording'))
  ) return 'recording';
  if (
    full.startsWith('/budgets/basecamp') ||
    full.startsWith('/budgets/new') ||
    full.startsWith('/budgets/edit/') ||
    (pathname.startsWith('/budgets/management') && search.includes('area=basecamp')) ||
    full.startsWith('/dailies/basecamp/') ||
    full.startsWith('/summary/basecamp')
  ) return 'basecamp';
  if (full.startsWith('/operations/')) return 'operations';
  if (full.startsWith('/master-budget')) return 'projects';
  if (pathname.startsWith('/budgets/management') && !search.includes('area=')) return 'projects';
  if (full.startsWith('/tenders')) return 'tenders';
  if (full.startsWith('/projects/') || pathname === '/projects') return 'projects';
  if (pathname === '/administration') return 'administration';
  if (isFinanceRoute(pathname, search)) return 'finance';
  if (full.startsWith('/inventory')) return 'inventory';
  if (full.startsWith('/supply-chain')) return 'supply-chain';
  if (full.startsWith('/catering')) return 'catering';
  if (full.startsWith('/hr/')) return 'hr';
  if (full.startsWith('/platform')) return null;
  if (full.startsWith('/master-data/') || full.startsWith('/admin/users') || full.startsWith('/admin/backups') || full.startsWith('/company/backups') || full.startsWith('/access-control') || full.startsWith('/settings/')) return 'settings';
  if (full.startsWith('/help/')) return 'help';
  return null;
};

export const getDesktopActiveMenuId = (pathname, search = '') => {
  return mapActiveMenuIdForGrouping(getRouteMenuId(pathname, search));
};

export { isFinanceRoute, resolveMobileLevel1MenuId };
